<script setup lang="ts">
import CenterModal from "~/components/Common/Modals/CenterModal.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tooltip from "~/components/Common/Tooltip.vue";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import LoadButton from "~/components/Common/Buttons/LoadButton.vue";
import PriceInput from "~/components/Common/Inputs/PriceInput.vue";
import { useDateChoiceStore } from "~/store/date-choice/date-choice.store";

/**
 * "Modifier le prix" on a pending registration: the cost is inherited from the source as long as no
 * session is chosen, and counts towards the validated budget from creation onwards (US3).
 */
const props = defineProps<{
  projectUserId: string;
  priceExclTax: number | null;
}>();

const emit = defineEmits<{ hide: []; saved: [] }>();

const dateChoiceStore = useDateChoiceStore();

const price = ref<number | null>(props.priceExclTax);
const isSaving = ref(false);

// PriceInput emits null on an empty field: Number(null) would be a valid 0 and let it be saved blind.
const canSave = computed(() => typeof price.value === "number" && Number.isFinite(price.value) && price.value >= 0);

async function save() {
  if (!canSave.value) {
    isSaving.value = false;
    return;
  }
  const ok = await dateChoiceStore.updatePendingRegistrationPrice(props.projectUserId, Number(price.value));
  isSaving.value = false;
  if (ok) emit("saved");
}
</script>

<template>
  <CenterModal style="width: 488px" @hide="emit('hide')">
    <template #body>
      <SvgIcon
        class="absolute right-4 top-4 text-secondary-color cursor-pointer w-[24px] h-[24px]"
        name="close"
        @click="emit('hide')"
      ></SvgIcon>
      <div class="flex flex-col gap-[24px] p-[24px]">
        <h3 class="heading_3_demi text-primary-color">Modifier le prix</h3>

        <div class="flex items-center justify-between gap-[16px]">
          <div class="flex items-center gap-[4px]">
            <label class="body_regular text-secondary-color">Prix HT</label>
            <Tooltip position="bot-right" append-to-body minified :svg-width="16">
              <template #content>
                <div class="flex flex-col w-[380px]">
                  <span class="body_regular">Le prix est par défaut attribué au Budget Entreprise.</span>
                  <span class="body_regular">
                    L'allocation par mode de financement interviendra lorsque l'inscription sera liée à une session.
                  </span>
                </div>
              </template>
            </Tooltip>
          </div>
          <!-- Fixed width: PriceInput is `flex-1`, which collapses its 40px height outside a row container -->
          <div class="w-[240px]">
            <PriceInput v-model="price" type="number" min="0" placeholder="-" />
          </div>
        </div>

        <div class="flex gap-[16px] justify-end pt-[16px] border-t border-stroke-color">
          <AppButton type="outlined" variant="error" label="Annuler" :disabled="isSaving" @click="emit('hide')" />
          <LoadButton v-model="isSaving" text-confirm="Enregistrer" :disabled="!canSave" @click="save" />
        </div>
      </div>
    </template>
  </CenterModal>
</template>
